import {
  composeSettingItemConfigByIdMap,
  type GenericFlattenedSettingItemConfig,
} from './composeSettingItemConfigByIdMap.ts'
import {
  composeSettingStorageKey,
  readSettingStorage,
  type SettingPersistentStorageKey,
  writeSettingStorage,
} from './settingPersistentStorage.ts'
import {
  type FlattenedSettingValues,
  type SettingsConfig,
} from './settingsConfigSchema.ts'

type SettingStateStoreListener = () => void

export type SettingStateUpdater<RootConfig extends SettingsConfig> = (
  values: FlattenedSettingValues<RootConfig>,
) => FlattenedSettingValues<RootConfig>

export type SettingStateStoreSetState<RootConfig extends SettingsConfig> = (
  updater: SettingStateUpdater<RootConfig>,
) => void

export type SettingStateStore<
  RootConfig extends SettingsConfig = SettingsConfig,
> = Readonly<{
  storageKey: SettingPersistentStorageKey
  settingsConfig: RootConfig
  itemConfigByIdMap: GenericFlattenedSettingItemConfig
  getState: () => FlattenedSettingValues<RootConfig>
  setState: SettingStateStoreSetState<RootConfig>
  subscribe: (listener: SettingStateStoreListener) => () => void
}>

export const createSettingStateStore = <RootConfig extends SettingsConfig>(
  settingsConfig: RootConfig,
): SettingStateStore<RootConfig> => {
  const storageKey = composeSettingStorageKey(settingsConfig.settingsId)

  let currentValues = readSettingStorage(storageKey, settingsConfig)

  const listeners = new Set<SettingStateStoreListener>()

  const getState = () => currentValues

  const setState = (updater: SettingStateUpdater<RootConfig>) => {
    // Compose a new state
    const newState =
      typeof updater === 'function' ? updater(currentValues) : updater
    currentValues = newState
    writeSettingStorage(storageKey, newState)
    // Notify all listeners
    listeners.forEach((listener) => listener())
  }

  const subscribe = (listener: SettingStateStoreListener) => {
    listeners.add(listener)
    // Return unsubscribe function
    return () => listeners.delete(listener)
  }

  // Prevent mutating store object directly.
  return Object.freeze({
    storageKey,
    settingsConfig,
    itemConfigByIdMap: composeSettingItemConfigByIdMap(settingsConfig),
    getState,
    setState,
    subscribe,
  })
}

const stateStoreCache = new Map<string, SettingStateStore>()

/**
 * Get a setting state store from the cache.
 * If not found, create a new store and cache it.
 */
export const getSettingStateStore = <RootConfig extends SettingsConfig>(
  settingsConfig: RootConfig,
): SettingStateStore<RootConfig> => {
  const storeCacheKey = settingsConfig.settingsId

  // If the store is already created, return the cached store.
  const cachedStore = stateStoreCache.get(storeCacheKey)
  if (cachedStore) {
    return cachedStore as unknown as SettingStateStore<RootConfig>
  }

  const store = createSettingStateStore(settingsConfig)

  stateStoreCache.set(
    storeCacheKey,
    // Widen the type to fit in the cache
    store as unknown as SettingStateStore,
  )

  return store
}

// Expose the setting stores cache for debugging.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.SETTING_STATE_STORES_CACHE = stateStoreCache
